import { generateQueryKey } from '../utils/react-query-utils';
import type { CashFlowProjectionDto, SaEngineRequestData } from './generated/client';

interface ISaeExternalAssetItemSettingsOptions {
    id?: number;
}

enum QueryKeyIdentifier {
    ADVISORY_NEEDS = 'ADVISORY_NEEDS',
    ADVISORY_NEEDS_FOR_TALKING_POINT = 'ADVISORY_NEEDS_FOR_TALKING_POINT',
    ADVISORY_NEED_TASKS = 'ADVISORY_NEED_TASKS',
    APPLICATION_VERSION = 'APPLICATION_VERSION',
    BALANCE_SHEET = 'BALANCE_SHEET',
    BUSINESS_RELATIONSHIP_WITH_MOST_ASSETS_UNDER_MANAGEMENT = 'BUSINESS_RELATIONSHIP_WITH_MOST_ASSETS_UNDER_MANAGEMENT',
    CALCULATORS_URLS = 'CALCULATORS_URLS',
    CASHFLOW = 'CASHFLOW',
    CHECK_DOCUMENT = 'CHECK_DOCUMENT',
    CLIENT = 'CLIENT',
    CLIENT_BY_BUSINESS_RELATIONSHIP_UUID = 'CLIENT_BY_BUSINESS_RELATIONSHIP_UUID',
    CLIENT_PACKAGE = 'CLIENT_PACKAGE',
    CLIENT_PACKAGES_FOR_ALL_CLIENTS = 'CLIENT_PACKAGES_FOR_ALL_CLIENTS',
    CONFIGURATION = 'CONFIGURATION',
    CONTENT_BY_TALKING_POINT_ID_AND_DOMICILE = 'CONTENT_BY_TALKING_POINT_ID_AND_DOMICILE',
    CONTENT_BY_TOPIC_ID_AND_DOMICILE = 'CONTENT_BY_TOPIC_ID_AND_DOMICILE',
    DOCUMENTS = 'DOCUMENTS',
    DOMICILES = 'DOMICILES',
    DOWNLOAD_DOCUMENT = 'DOWNLOAD_DOCUMENT',
    ENABLED_DOMICILES = 'ENABLED_DOMICILES',
    EVENTS = 'EVENTS',
    FULL_TOPICS = 'FULL_TOPICS',
    MATURITY_EVENTS = 'MATURITY_EVENTS',
    NOTIFICATIONS = 'NOTIFICATIONS',
    NOTIFICATIONS_FOR_CLIENT = 'NOTIFICATIONS_FOR_CLIENT',
    PROJECTION = 'PROJECTION',
    SAE_CASH_EVENT_SETTINGS = 'SAE_CASH_EVENT_SETTINGS',
    SAE_CASH_FLOW_SETTINGS = 'SAE_CASH_FLOW_SETTINGS',
    SAE_CASH_FLOW_PROJECTION_GENERAL_SETTINGS = 'SAE_CASH_FLOW_PROJECTION_GENERAL_SETTINGS',
    SAE_INTERNAL_ASSET_SETTINGS = 'SAE_INTERNAL_ASSET_SETTINGS',
    SAE_EXTERNAL_ASSET_SETTINGS = 'SAE_EXTERNAL_ASSET_SETTINGS',
    SAE_EXTERNAL_ASSET_ITEM_SETTINGS = 'SAE_EXTERNAL_ASSET_ITEM_SETTINGS',
    SEARCH_PARTNER_BY_TERM = 'SEARCH_PARTNER_BY_TERM',
    SESSIONS = 'SESSIONS',
    SURPLUS_PANEL_SETTINGS = 'SURPLUS_PANEL_SETTINGS',
    TALKING_POINT_REFERENCE_TREE = 'TALKING_POINT_REFERENCE_TREE',
    TALKING_POINTS = 'TALKING_POINTS',
    TOOLS = 'TOOLS',
    TOPICS = 'TOPICS',
    USER = 'USER',
}

export const queryKeys = {
    advisoryNeeds: () => generateQueryKey(QueryKeyIdentifier.ADVISORY_NEEDS, 'LIST'),
    advisoryNeedsForTalkingPoint: (talkingPointId?: number) => generateQueryKey(QueryKeyIdentifier.ADVISORY_NEEDS_FOR_TALKING_POINT, 'LIST', talkingPointId),
    advisoryNeedTasks: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.ADVISORY_NEED_TASKS, 'ITEM', partnerUuid),
    applicationVersion: () => generateQueryKey(QueryKeyIdentifier.APPLICATION_VERSION, 'ITEM'),
    balanceSheet: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.BALANCE_SHEET, 'ITEM', partnerUuid),
    businessRelationshipWithMostAssetsUnderManagement: (partnerUuid?: string, brUuids?: string[]) =>
        generateQueryKey(QueryKeyIdentifier.BUSINESS_RELATIONSHIP_WITH_MOST_ASSETS_UNDER_MANAGEMENT, 'ITEM', partnerUuid, brUuids),
    calculatorsUrls: () => generateQueryKey(QueryKeyIdentifier.CALCULATORS_URLS, 'ITEM'),
    cashFlow: (partnerUuid?: string, options?: CashFlowProjectionDto) => generateQueryKey(QueryKeyIdentifier.CASHFLOW, 'ITEM', partnerUuid, options),
    checkDocument: (documentId?: number) => generateQueryKey(QueryKeyIdentifier.CHECK_DOCUMENT, 'ITEM', documentId),
    client: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.CLIENT, 'ITEM', partnerUuid),
    clientByBusinessRelationshipUuid: (businessRelationshipUuid: string) =>
        generateQueryKey(QueryKeyIdentifier.CLIENT_BY_BUSINESS_RELATIONSHIP_UUID, 'ITEM', businessRelationshipUuid),
    clientPackage: (packageId: number) => generateQueryKey(QueryKeyIdentifier.CLIENT_PACKAGE, 'ITEM', packageId),
    clientPackagesForClient: (clientId?: number) => generateQueryKey(QueryKeyIdentifier.CLIENT_PACKAGES_FOR_ALL_CLIENTS, 'LIST', clientId),
    configuration: () => generateQueryKey(QueryKeyIdentifier.CONFIGURATION, 'ITEM'),
    contentByTalkingPointIdAndDomicile: (talkingPointId?: number, domicile?: string) =>
        generateQueryKey(QueryKeyIdentifier.CONTENT_BY_TALKING_POINT_ID_AND_DOMICILE, 'LIST', talkingPointId, domicile),
    contentByTopicIdAndDomicile: (topicId?: number, domicile?: string) =>
        generateQueryKey(QueryKeyIdentifier.CONTENT_BY_TOPIC_ID_AND_DOMICILE, 'LIST', topicId, domicile),
    documents: () => generateQueryKey(QueryKeyIdentifier.DOCUMENTS, 'LIST'),
    domiciles: () => generateQueryKey(QueryKeyIdentifier.DOMICILES, 'LIST'),
    downloadDocument: (documentId: number, documentLanguage: string) =>
        generateQueryKey(QueryKeyIdentifier.DOWNLOAD_DOCUMENT, 'ITEM', documentId, documentLanguage),
    enabledDomiciles: () => generateQueryKey(QueryKeyIdentifier.ENABLED_DOMICILES, 'LIST'),
    events: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.EVENTS, 'ITEM', partnerUuid),
    fullTopics: (topicId?: number) => generateQueryKey(QueryKeyIdentifier.FULL_TOPICS, 'ITEM', topicId),
    notifications: () => generateQueryKey(QueryKeyIdentifier.NOTIFICATIONS, 'LIST'),
    notificationsForClient: (clientId?: number) => generateQueryKey(QueryKeyIdentifier.NOTIFICATIONS_FOR_CLIENT, 'ITEM', clientId),
    projection: (partnerUuid?: string, options?: SaEngineRequestData) => generateQueryKey(QueryKeyIdentifier.PROJECTION, 'ITEM', partnerUuid, options),
    saeCashEventSettings: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.SAE_CASH_EVENT_SETTINGS, 'ITEM', partnerUuid),
    saeCashFlowSettings: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.SAE_CASH_FLOW_SETTINGS, 'ITEM', partnerUuid),
    saeCashFlowProjectionGeneralSettings: (partnerUuid?: string) =>
        generateQueryKey(QueryKeyIdentifier.SAE_CASH_FLOW_PROJECTION_GENERAL_SETTINGS, 'ITEM', partnerUuid),
    saeInternalAssetSettings: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.SAE_INTERNAL_ASSET_SETTINGS, 'ITEM', partnerUuid),
    saeExternalAssetSettings: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.SAE_EXTERNAL_ASSET_SETTINGS, 'ITEM', partnerUuid),
    saeExternalAssetItemSettings: (options?: ISaeExternalAssetItemSettingsOptions) =>
        generateQueryKey(QueryKeyIdentifier.SAE_EXTERNAL_ASSET_ITEM_SETTINGS, 'ITEM', options),
    searchPartnerByTerm: (searchTerm: string) => generateQueryKey(QueryKeyIdentifier.SEARCH_PARTNER_BY_TERM, 'LIST', searchTerm),
    sessions: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.SESSIONS, 'ITEM', partnerUuid),
    surplusPanelSettings: (partnerUuid?: string) => generateQueryKey(QueryKeyIdentifier.SURPLUS_PANEL_SETTINGS, 'ITEM', partnerUuid),
    talkingPointReferenceTree: (talkingPointId?: number) => generateQueryKey(QueryKeyIdentifier.TALKING_POINT_REFERENCE_TREE, 'LIST', talkingPointId),
    talkingPoints: () => generateQueryKey(QueryKeyIdentifier.TALKING_POINTS, 'LIST'),
    tools: () => generateQueryKey(QueryKeyIdentifier.TOOLS, 'ITEM'),
    topic: (topicId?: number) => generateQueryKey(QueryKeyIdentifier.TOPICS, 'ITEM', topicId),
    topics: () => generateQueryKey(QueryKeyIdentifier.TOPICS, 'LIST'),
    user: () => generateQueryKey(QueryKeyIdentifier.USER, 'ITEM'),
};
